import React, { useState, useEffect } from "react";
import authHeader from "../services/AuthHeader";
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { BsToggleOn, BsToggleOff } from "react-icons/bs";
import { FaPen, FaFileDownload, FaFileExcel } from "react-icons/fa";
import { Link } from 'react-router-dom';
import { CSVLink } from "react-csv";
import { VscClearAll } from "react-icons/vsc";
import moment from 'moment';
import AccessibleLink from './AccessibleLink';
import {
  FormGroup,
  FormControl
} from 'react-bootstrap';
import confirmDialog from '../services/confirmDialog';
import { formatDate } from '../util/Helper';
import {
  isElectionActive,
  isElectionStatusToggleDisabled,
} from '../util/electionStatus';
import { buildBallotResults } from '../util/manageElectionResults';

const excelMimeType = 'application/vnd.ms-excel;charset=utf-8;';

const escapeXml = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const sanitizeFileName = (value = 'election-results') =>
  value
    .replace(/[\\/:*?"<>|]/g, ' ')
    .trim()
    .replace(/\s+/g, '-')
    .toLowerCase();

const sanitizeWorksheetName = (value = 'Results') =>
  value
    .replace(/[:\\/?*[\]]/g, ' ')
    .trim()
    .slice(0, 31) || 'Results';

const buildResultsWorkbook = (electionName, results) => {
  const expandedColumns = 2;
  const columnDefinitions =
    '<Column ss:AutoFitWidth="0" ss:Width="260"/>'
    + '<Column ss:AutoFitWidth="0" ss:Width="85"/>';
  const workbookRows = [];

  if (results.length) {
    results.forEach((ballot, ballotIndex) => {
      workbookRows.push(`
        <Row>
          <Cell ss:StyleID="BallotTitle" ss:MergeAcross="1">
            <Data ss:Type="String">${escapeXml(ballot.ballotName)}</Data>
          </Cell>
        </Row>
      `);
      workbookRows.push(`
        <Row>
          <Cell ss:StyleID="ColumnHeader">
            <Data ss:Type="String">Ballot Choice</Data>
          </Cell>
          <Cell ss:StyleID="ColumnHeader">
            <Data ss:Type="String">Votes</Data>
          </Cell>
        </Row>
      `);

      ballot.rows.forEach((row) => {
        const textStyle = row.isLeader ? 'WinnerTextCell' : 'TextCell';
        const numberStyle = row.isLeader ? 'WinnerNumberCell' : 'NumberCell';

        workbookRows.push(`
          <Row>
            <Cell ss:StyleID="${textStyle}">
              <Data ss:Type="String">${escapeXml(row.label)}</Data>
            </Cell>
            <Cell ss:StyleID="${numberStyle}">
              <Data ss:Type="Number">${row.count}</Data>
            </Cell>
          </Row>
        `);
      });

      if (ballotIndex < results.length - 1) {
        workbookRows.push('<Row/>');
      }
    });
  } else {
    workbookRows.push(`
      <Row>
        <Cell ss:StyleID="BallotTitle" ss:MergeAcross="1">
          <Data ss:Type="String">${escapeXml(electionName)}</Data>
        </Cell>
      </Row>
    `);
    workbookRows.push(`
      <Row>
        <Cell ss:StyleID="TextCell" ss:MergeAcross="1">
          <Data ss:Type="String">No results available.</Data>
        </Cell>
      </Row>
    `);
  }

  const expandedRows = Math.max(workbookRows.length, 2);

  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook
  xmlns="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:o="urn:schemas-microsoft-com:office:office"
  xmlns:x="urn:schemas-microsoft-com:office:excel"
  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:html="http://www.w3.org/TR/REC-html40"
>
  <DocumentProperties xmlns="urn:schemas-microsoft-com:office:office">
    <Title>${escapeXml(electionName)} Results</Title>
  </DocumentProperties>
  <Styles>
    <Style ss:ID="BaseCell">
      <Alignment ss:Vertical="Center" ss:WrapText="1"/>
      <Borders>
        <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/>
      </Borders>
    </Style>
    <Style ss:ID="BallotTitle" ss:Parent="BaseCell">
      <Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:WrapText="1"/>
      <Font ss:Bold="1" ss:Size="12"/>
      <Interior ss:Color="#E7E1D6" ss:Pattern="Solid"/>
    </Style>
    <Style ss:ID="ColumnHeader" ss:Parent="BaseCell">
      <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
      <Font ss:Bold="1"/>
      <Interior ss:Color="#D9D9D9" ss:Pattern="Solid"/>
    </Style>
    <Style ss:ID="TextCell" ss:Parent="BaseCell"/>
    <Style ss:ID="NumberCell" ss:Parent="BaseCell">
      <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
    </Style>
    <Style ss:ID="WinnerTextCell" ss:Parent="TextCell">
      <Font ss:Bold="1"/>
      <Interior ss:Color="#F9E6A7" ss:Pattern="Solid"/>
    </Style>
    <Style ss:ID="WinnerNumberCell" ss:Parent="NumberCell">
      <Font ss:Bold="1"/>
      <Interior ss:Color="#F9E6A7" ss:Pattern="Solid"/>
    </Style>
  </Styles>
  <Worksheet ss:Name="${escapeXml(sanitizeWorksheetName(electionName))}">
    <Table ss:ExpandedColumnCount="${expandedColumns}" ss:ExpandedRowCount="${expandedRows}" x:FullColumns="1" x:FullRows="1">
      ${columnDefinitions}
      ${workbookRows.join('')}
    </Table>
  </Worksheet>
</Workbook>`;
};

const ManageElection = (props) => {
  let history = useHistory();
  const [studentCnt, setStudentCnt] = useState([]);
  const [votesCastCnt, setVotesCastCnt] = useState(0);
  const [csvData, setCsvData] = useState([]);
  const [ballotResults, setBallotResults] = useState(null);
  const [resultBallots, setResultBallots] = useState([]);
  const [resultRows, setResultRows] = useState([]);
  const [resultsLoaded, setResultsLoaded] = useState(false);
  const [ballotsLoaded, setBallotsLoaded] = useState(false);
  const [editStartDate, setEditStartDate] = useState(false);
  const [editEndDate, setEditEndDate] = useState(false);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [lock, setLock] = useState();
  const [activeStatus, setActiveStatus] = useState(false);
  const emptyDateValue = '01/01/0001';
  const electionName = Array.isArray(props.electionData) && props.electionData.length
    ? props.electionData[0].electionName
    : 'Election';

  const toDisplayDate = (value) => {
    if (!value) {
      return '';
    }

    const formatted = formatDate(value);
    return formatted === 'Invalid date' || formatted === emptyDateValue
      ? ''
      : formatted;
  };

  const toInputDate = (value) => {
    if (!value) {
      return '';
    }

    const parsed = moment.utc(value);
    return parsed.isValid() && parsed.year() > 1 ? parsed.format('YYYY-MM-DD') : '';
  };

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_BASE + 'studentcount/', {
        headers: authHeader(),
      })
      .then((res) => {
        setStudentCnt(res.data.cnt);
      });
  }, []);

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_BASE + 'voterecordcount/', {
        headers: authHeader(),
      })
      .then((res) => {
        setVotesCastCnt(res.data.votesCast);
      });
  }, []);

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_BASE + 'studentsvoted/', {
        headers: authHeader(),
      })
      .then((res) => {
        setCsvData(res.data);
      });
  }, []);

  useEffect(() => {
    if(props.electionData !== undefined && props.electionData.length !== 0) {
      setStartDate(props.electionData[0].startActiveAt);
      setEndDate(props.electionData[0].endActiveAt);
      setLock(props.electionData[0].locked);
      setActiveStatus(props.electionData[0].activeStatus);
    }
  }, [props.electionData]);

  /* get election ballot configuration used to enrich results */
  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_BASE + 'election/', {
        headers: authHeader(),
      })
      .then((electionRes) => {
        setResultBallots(electionRes.data.allBallots || []);
      })
      .catch(() => {
        setResultBallots([]);
      })
      .finally(() => {
        setBallotsLoaded(true);
      });
  }, []);

  /* get voting results as soon as they are available */
  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_BASE + 'votingresults/', {
        headers: authHeader(),
      })
      .then((votingRes) => {
        setResultRows(votingRes.data || []);
      })
      .catch(() => {
        setResultRows([]);
      })
      .finally(() => {
        setResultsLoaded(true);
      });
  }, []);

  useEffect(() => {
    if (!resultsLoaded) {
      return;
    }

    if (!resultRows.length && !ballotsLoaded) {
      return;
    }

    setBallotResults(buildBallotResults(resultBallots, resultRows));
  }, [ballotsLoaded, resultBallots, resultRows, resultsLoaded]);

  const isLockToggleDisabled = isElectionStatusToggleDisabled({
    locked: lock,
    activeStatus: activeStatus,
    startActiveAt: startDate,
    endActiveAt: endDate,
  });
  const isElectionCurrentlyActive = isElectionActive({
    locked: lock,
    activeStatus: activeStatus,
  });
  const lockToggleAriaLabel = isLockToggleDisabled
    ? 'Election cannot be deactivated during its scheduled active dates'
    : isElectionCurrentlyActive
      ? 'Deactivate election'
      : 'Activate election';

  const clearBallotResults = () => {
    setBallotResults((currentResults) => {
      if (!currentResults) {
        return currentResults;
      }

      return currentResults.map((ballot) => ({
        ...ballot,
        rows: ballot.rows.map((row) => ({
          ...row,
          count: 0,
          isLeader: false,
        })),
      }));
    });
    setResultRows([]);
    setResultsLoaded(true);
  };

  const exportResultsToExcel = () => {
    if (!ballotResults || !ballotResults.length) {
      return;
    }

    const workbook = buildResultsWorkbook(electionName, ballotResults);
    const blob = new Blob([workbook], { type: excelMimeType });
    const downloadLink = document.createElement('a');
    const objectUrl = window.URL.createObjectURL(blob);

    downloadLink.href = objectUrl;
    downloadLink.download = `${sanitizeFileName(electionName)}-results-${moment().format('YYYY-MM-DD')}.xls`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    window.URL.revokeObjectURL(objectUrl);
  };

  /* delete all results */
  const deleteAll = async () => {
    const result = await confirmDialog('Are you really sure?', {
      confirmLabel: 'Delete',
      confirmStyle: 'danger',
      title: 'Clear Votes',
    });
    if(result) {
      axios
      .delete(
        process.env.REACT_APP_API_BASE + 'votingresults/', { headers: authHeader() }
      )
      .then(window.location.reload())
    }
  };

  const hasOwnProperty = (object, key) =>
    Object.prototype.hasOwnProperty.call(object, key);

 /* update elecion settings */
 const updateElection = (updElection) => {
  return axios
    .put(process.env.REACT_APP_API_BASE + 'election/1', updElection, {
      headers: authHeader(),
    })
 }

  const buildElectionUpdate = (overrides = {}) => ({
    StartActiveAt: hasOwnProperty(overrides, 'StartActiveAt')
      ? overrides.StartActiveAt
      : startDate || null,
    EndActiveAt: hasOwnProperty(overrides, 'EndActiveAt')
      ? overrides.EndActiveAt
      : endDate || null,
    Locked: hasOwnProperty(overrides, 'Locked')
      ? overrides.Locked
      : !!lock,
  });

  const syncLocalElectionData = (changes) => {
    props.setElectionData((currentElectionData) =>
      currentElectionData.map((election, index) =>
        index === 0
          ? {
              ...election,
              ...changes,
            }
          : election
      )
    );
  };

  const handleChangeStartDate = function (e) {
    setEditStartDate(false);
    if (!e) {
      return;
    }

    var dateMomentObject = moment(e, 'YYYY-MM-DD').set("hour", 3).set("minute", 0); 
    var dateObject = dateMomentObject.toDate();
    const updatedIsoValue = dateObject.toISOString();
    setStartDate(updatedIsoValue);
 
    const updElection = buildElectionUpdate({
      StartActiveAt: updatedIsoValue,
    });
    updateElection(updElection);
    syncLocalElectionData({
      startActiveAt: updatedIsoValue,
    });
  };

  const handleChangeEndDate = function (e) {
    setEditEndDate(false);
    if (!e) {
      return;
    }

    var dateMomentObject = moment(e, 'YYYY-MM-DD').set("hour", 12).set("minute", 0); 
    var dateObject = dateMomentObject.toDate();
    const updatedIsoValue = dateObject.toISOString();
    setEndDate(updatedIsoValue);

    const updElection = buildElectionUpdate({
      EndActiveAt: updatedIsoValue,
    });
    updateElection(updElection);
    syncLocalElectionData({
      endActiveAt: updatedIsoValue,
    });
  };

  /* activate/deactivate election */
  const handleChangeLock = async () => {
    if (isLockToggleDisabled) {
      return;
    }

    var thisLock = !lock;
    const updElection = buildElectionUpdate({
      Locked: thisLock,
    });
    if(!lock) {
      const result = await confirmDialog(
        'Activating the election will clear out the mocked votes. Are you sure?',
        {
          confirmLabel: 'Activate',
          confirmStyle: 'primary',
          title: 'Activate Election',
        }
      );
      if(result) {
        await axios.delete(
          process.env.REACT_APP_API_BASE + 'votingresults/', { headers: authHeader() }
        );
        await updateElection(updElection);
        syncLocalElectionData({
          locked: thisLock,
        });
        setLock(thisLock);
        props.handlePost();
        clearBallotResults();
        setVotesCastCnt(0);
      }
    } else {
      await updateElection(updElection);
      syncLocalElectionData({
        locked: thisLock,
      });
      setLock(thisLock);
      props.handlePost();
    }
  };

  return props.electionData ? (
    <>
      <h1 className='h2'>Manage Elections</h1>
      <h2 className='h3'>{electionName}</h2>
      <table className='table table-striped'>
        <thead>
          <tr key='0'>
            <th key='name'>Election</th>
            <th key='status'>Status</th>
            <th key='active-from'>Start Date</th>
            <th key='active-to'>End Date</th>
            <th key='edit'></th>
            <th key='action'></th>
          </tr>
        </thead>
        <tbody>
          {props.electionData.map((election, i) => {
            return (
              <tr key={'election_ '+i}>
                <td key={'name_' + i}>{election.electionName}</td>
                <td key={'status_' + i}>{isElectionCurrentlyActive ? 'Active' : 'Inactive'}</td>
                <td key={'start_' + i}>
                {editStartDate ? (
                <FormGroup controlId='date'>
                  <FormControl
                    type='date'
                    className='startActiveAt'
                    name='startActiveAt'
                    aria-label='Election start date'
                    value={toInputDate(startDate)}
                    onBlur={() => setEditStartDate(false)}
                    onChange={(evt) =>
                      handleChangeStartDate(evt.target.value)
                    }
                  />
                  <FormControl.Feedback />
                  </FormGroup>) : (
                    <div
                      role='button'
                      tabIndex={0}
                      aria-label='Edit election start date'
                      onDoubleClick={() => setEditStartDate(true)}
                      onKeyDown={(evt) => {
                        if (evt.key === 'Enter' || evt.key === ' ') {
                          evt.preventDefault();
                          setEditStartDate(true);
                        }
                      }}
                    >
                      {toDisplayDate(startDate)}
                    </div>
                  )}
                </td>
                <td key={'end_' + i}>
                {editEndDate ? (
                <FormGroup controlId='date'>
                  <FormControl
                    type='date'
                    className='endActiveAt'
                    name='endActiveAt'
                    aria-label='Election end date'
                    value={toInputDate(endDate)}
                    onBlur={() => setEditEndDate(false)}
                    onChange={(evt) =>
                      handleChangeEndDate(evt.target.value)
                    }
                  />
                  <FormControl.Feedback />
                  </FormGroup>) : (
                    <div
                      role='button'
                      tabIndex={0}
                      aria-label='Edit election end date'
                      onDoubleClick={() => setEditEndDate(true)}
                      onKeyDown={(evt) => {
                        if (evt.key === 'Enter' || evt.key === ' ') {
                          evt.preventDefault();
                          setEditEndDate(true);
                        }
                      }}
                    >
                      {toDisplayDate(endDate)}
                    </div>
                  )}
                </td>
                <td key={'edit_' + i}>
                  {!isElectionCurrentlyActive ? (
                    <AccessibleLink
                      component={Link}
                      to="/editelection"
                      className='link-no-border icon-link-black'
                      aria-label='Edit election ballots'
                      description='Navigates to the election ballot editor.'
                      onClick={() => {
                        history.push({
                          pathname: '/editelection',
                          state: {
                            // location state
                            electionData: props.electionData,
                          },
                        });
                      }}
                    >
                      <FaPen
                        size={26}
                        style={{verticalAlign:'middle'}}
                        aria-hidden='true'
                      />
                    </AccessibleLink>
                  ) : null}
                </td>
                <td key={'action_' + i}>
                  <button
                    type='button'
                    className='icon-button'
                    aria-label={lockToggleAriaLabel}
                    title={lockToggleAriaLabel}
                    disabled={isLockToggleDisabled}
                    onClick={() => {
                      handleChangeLock()
                    }}
                  >
                    {isElectionCurrentlyActive ? (
                      <BsToggleOn
                        size={36}
                        style={{verticalAlign:'middle'}} 
                        title='election is active'
                        aria-hidden='true'
                      />
                    ) : (
                      <BsToggleOff
                        size={36}
                        style={{verticalAlign:'middle'}} 
                        title='election is inactive'
                        aria-hidden='true'
                      />
                    )}
                  </button>
                </td>
                <td key={'delet_' + i}>
                <button
                    type='button'
                    className='icon-button'
                    aria-label='Clear current votes'
                    onClick={deleteAll}>
                    <VscClearAll
                      size={26}
                      style={{verticalAlign:'middle'}}
                      title='clear current votes'
                      aria-hidden='true'
                    />
                </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className='bg-election-wrapper'>
      <table width='100%'>
        <tbody>
          <tr>
            <td width='85%'>
              Votes Cast
            </td>
            <td width='10%'>
              {votesCastCnt}/{studentCnt}
            </td>
            <td width='5%'>
              <AccessibleLink
                component={CSVLink}
                data={csvData}
                className='icon-link-black'
                aria-label='Download students who have voted'
                description='Downloads a CSV file listing the students who have voted.'
              >
                <FaFileDownload
                  size={26}
                  style={{verticalAlign:'middle'}}
                  aria-hidden='true'
                />
              </AccessibleLink>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
      <div className='bg-election-wrapper'>
        <div className='election-results-panel'>
          <div className='election-results-header'>
            <h2 className='h3'>Results</h2>
            <button
              type='button'
              className='icon-button election-results-export'
              aria-label='Export election results to Excel'
              title='Export election results to Excel'
              onClick={exportResultsToExcel}
              disabled={!ballotResults || !ballotResults.length}
            >
              <FaFileExcel size={24} aria-hidden='true' />
            </button>
          </div>
          {ballotResults === null ? (
            'Loading results...'
          ) : ballotResults.length ? (
            ballotResults.map((ballot) => (
              <div key={ballot.ballotId} className='bg-chart-wrapper'>
                <h3 className='election-results-title h4'>{ballot.ballotName}</h3>
                <table className='table table-striped election-results-table'>
                  <thead>
                    <tr>
                      <th scope='col'>Ballot Choice</th>
                      <th scope='col'>Votes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ballot.rows.length ? (
                      ballot.rows.map((row) => (
                        <tr
                          key={row.key}
                          className={row.isLeader ? 'election-results-winner' : ''}
                        >
                          <td>{row.label}</td>
                          <td className='election-results-count'>{row.count}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan='2'>No ballot choices are available.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ))
          ) : (
            'No ballot choices are available.'
          )}
        </div>
      </div>
    </>
  ) : (
    'Loading...'
  );
};
export default ManageElection;
